import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createAsyncActions } from '@icebreaker/shared/store';
import type {
  AdminEmailDeliveryListDto,
  AdminEmailDeliveryQuery,
  AdminEmailDeliveryRowDto,
  ApiErrorBody,
  EmailDeliveryStatus,
  EmailTemplateKey,
  Locale,
} from '@icebreaker/shared';

/**
 * E-POSTA GÖNDERİM GÖRÜNÜMÜ DEPOSU (panel · salt okunur).
 *
 * Ayrı dosyadır: `admin.slice.ts` dosya tavanına yakın ve bu yüzey onunla
 * hiçbir durumu paylaşmaz. Tek okuma aksiyonu vardır; mutasyon olmadığı için
 * invalidation da yoktur (`READ_ONLY_PREFIXES`).
 */

export const fetchEmailDeliveries = createAsyncActions<
  AdminEmailDeliveryQuery,
  AdminEmailDeliveryListDto
>('admin/emailDeliveriesFetch');

export interface EmailDeliveryFilters {
  readonly status: EmailDeliveryStatus | 'all';
  readonly template: EmailTemplateKey | 'all';
  readonly locale: Locale | 'all';
  /** `YYYY-MM-DD` — tarih girdisinin yerel biçimi; sunucuya ISO'ya çevrilir. */
  readonly from: string;
  readonly to: string;
  readonly page: number;
}

export const INITIAL_EMAIL_DELIVERY_FILTERS: EmailDeliveryFilters = {
  status: 'all',
  template: 'all',
  locale: 'all',
  from: '',
  to: '',
  page: 1,
};

export interface AdminEmailDeliveriesState {
  items: AdminEmailDeliveryRowDto[];
  page: number;
  total: number;
  hasNext: boolean;
  phase: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: ApiErrorBody | null;
  filters: EmailDeliveryFilters;
}

const initialState: AdminEmailDeliveriesState = {
  items: [],
  page: 1,
  total: 0,
  hasNext: false,
  phase: 'idle',
  error: null,
  filters: INITIAL_EMAIL_DELIVERY_FILTERS,
};

const slice = createSlice({
  name: 'adminEmailDeliveries',
  initialState,
  reducers: {
    /**
     * Süzgeç değişimi. Sayfa AÇIKÇA verilmedikçe 1'e döner: dördüncü sayfadayken
     * durumu daraltıp boş ekran görmek, kullanıcının "kayıt yok" diye okuduğu
     * bir yalandır.
     */
    filterChanged(state, action: PayloadAction<Partial<EmailDeliveryFilters>>) {
      const next = { ...state.filters, ...action.payload };
      state.filters = action.payload.page === undefined ? { ...next, page: 1 } : next;
    },
    filtersReset(state) {
      state.filters = INITIAL_EMAIL_DELIVERY_FILTERS;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailDeliveries.request, (state) => {
        state.phase = 'loading';
        state.error = null;
      })
      .addCase(fetchEmailDeliveries.success, (state, action) => {
        state.phase = 'succeeded';
        state.items = action.payload.items.map((item) => ({ ...item }));
        state.page = action.payload.page;
        state.total = action.payload.total;
        state.hasNext = action.payload.hasNext;
      })
      .addCase(fetchEmailDeliveries.failure, (state, action) => {
        state.phase = 'failed';
        state.error = action.payload;
      });
  },
});

export const {
  filterChanged: emailDeliveryFilterChanged,
  filtersReset: emailDeliveryFiltersReset,
} = slice.actions;
export const adminEmailDeliveriesReducer = slice.reducer;
